import React, { useMemo } from "react";
import { Message } from "@/types/posit";
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkNestHeaderBullets from '@/lib/remarkNestHeaderBullets';
import { FileText, FileSpreadsheet, FileType, Image, File } from 'lucide-react';
import { GradientResponseIndicator } from "./GradientResponseIndicator";
import ContentPaywall from "./ContentPaywall";
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import ConfidenceIndicator from '../chat/intelligence/ConfidenceIndicator';
import FollowUpSuggestions from '../chat/intelligence/FollowUpSuggestions';

interface ChatMessageProps {
  message: Message;
  userEmail?: string;
  firstName?: string;
  onExpandRequest?: (expandPrompt: string) => void;
  recommendedContent?: any[];
  creatorName?: string;
  isTyping?: boolean;
  onFollowUpClick?: (question: string) => void;
}

const ChatMessage = ({ 
  message, 
  onExpandRequest, 
  recommendedContent, 
  creatorName, 
  isTyping = false,
  onFollowUpClick 
}: ChatMessageProps) => {
  const isUser = message.role === "user";

  // Map [n] -> specific citation object by first-appearance order
  const orderedUniqueBracketRefs = useMemo(() => {
    try {
      const matches = Array.from(String(message?.content || '').matchAll(/\[(\d+)\]/g)).map(m => parseInt(m[1], 10));
      const seen = new Set<number>();
      const ordered: number[] = [];
      for (const n of matches) {
        if (!Number.isNaN(n) && !seen.has(n)) {
          seen.add(n);
          ordered.push(n);
        }
      }
      return ordered;
    } catch {
      return [] as number[];
    }
  }, [message?.content]);

  const refNumberToCitation = useMemo(() => {
    const map = new Map<number, any>();
    if (Array.isArray(message.citations) && message.citations.length > 0) {
      orderedUniqueBracketRefs.forEach((refNum, idx) => {
        const citation = (message as any).citations[idx];
        if (citation) map.set(refNum, citation);
      });
    }
    return map;
  }, [message.citations, orderedUniqueBracketRefs]);

  // Map original reference numbers -> display numbers (1..N by first mention)
  const displayNumberMap = useMemo(() => {
    const map = new Map<number, number>();
    orderedUniqueBracketRefs.forEach((refNum, idx) => map.set(refNum, idx + 1));
    return map;
  }, [orderedUniqueBracketRefs]);

  const InlineCitation = ({ refNum }: { refNum: number }) => {
    const citation = refNumberToCitation.get(refNum);
    const content = (
      <div>
        <div className="text-xs font-semibold mb-1">{citation?.title || 'Referenced context'}</div>
        {citation?.summary ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.summary}</p>
        ) : citation?.snippet ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.snippet}</p>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
        )}
      </div>
    );

    return (
      <span className="align-super ml-0.5 inline-flex">
        <span className="hidden md:inline">
          <HoverCard>
            <HoverCardTrigger asChild>
              <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${refNum}`}>
                <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none ml-1 mr-1">
                  {refNum}
                </span>
              </sup>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="center" className="w-80">
              {content}
            </HoverCardContent>
          </HoverCard>
        </span>
        <span className="inline md:hidden">
          <Popover>
            <PopoverTrigger asChild>
              <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${refNum}`}>
                <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none ml-1 mr-1">
                  {refNum}
                </span>
              </sup>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-80">
              {content}
            </PopoverContent>
          </Popover>
        </span>
      </span>
    );
  };

  // Replace [n] markers with compact number pills when citations exist
  const transformTextWithPills = (input: string): React.ReactNode[] => {
    if (!Array.isArray(message.citations) || message.citations.length === 0) return [input];
    const parts = input.split(/(\[\d+\])/g);
    const output: React.ReactNode[] = [];
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const match = part.match(/^\[(\d+)\]$/);
      if (match) {
        const num = parseInt(match[1], 10);
        const display = displayNumberMap.get(num) || num;
        const pill = (
          <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] text-[0.65em] leading-none ml-1 mr-1 align-super">
            {display}
          </span>
        );
        output.push(
          <span key={`pill-${i}`} className="inline-flex align-super">
            <span className="hidden md:inline">
              <HoverCard>
                <HoverCardTrigger asChild>
                  {pill}
                </HoverCardTrigger>
                <HoverCardContent side="top" align="center" className="w-80">
                  <div>
                    <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(num)?.title || 'Referenced context'}</div>
                    {refNumberToCitation.get(num)?.summary ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(num)?.summary}</p>
                    ) : refNumberToCitation.get(num)?.snippet ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(num)?.snippet}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </span>
            <span className="inline md:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  {pill}
                </PopoverTrigger>
                <PopoverContent side="top" align="center" className="w-80">
                  <div>
                    <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(num)?.title || 'Referenced context'}</div>
                    {refNumberToCitation.get(num)?.summary ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(num)?.summary}</p>
                    ) : refNumberToCitation.get(num)?.snippet ? (
                      <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(num)?.snippet}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          </span>
        );
      } else if (part) {
        output.push(part);
      }
    }
    return output;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return <FileText className="h-4 w-4 text-blue-500" />;
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
    } else if (mimeType === 'application/pdf') {
      return <FileType className="h-4 w-4 text-red-500" />;
    } else if (mimeType.startsWith('image/')) {
      return <Image className="h-4 w-4 text-purple-500" />;
    } else {
      return <File className="h-4 w-4 text-gray-500" />;
    }
  };

  // Handle follow-up click
  const handleFollowUpClick = (question: string) => {
    if (onFollowUpClick) {
      onFollowUpClick(question);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "flex w-full",
          isUser ? "justify-end" : "justify-start"
        )}
      >
        <div className={cn(
          "max-w-[85%] px-4 py-2",
          isUser 
            ? "text-white bg-primary/90 rounded-lg border border-primary/10 selection:bg-white/30 selection:text-white"
            : "text-foreground bg-transparent selection:bg-primary/60 selection:text-white"
        )}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
              <ReactMarkdown 
              className="prose dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
              remarkPlugins={[remarkGfm, remarkBreaks, remarkNestHeaderBullets]}
              components={{
                 p: ({ children }) => (
                  <p className="mb-2 last:mb-0">
                     {React.Children.map(children as any, (child) => typeof child === 'string' ? <>{transformTextWithPills(child)}</> : child)}
                  </p>
                ),
                 ul: ({ children }) => <ul className="list-disc pl-6 space-y-1 mb-2 last:mb-0">{children}</ul>,
                 ol: ({ children }) => <ol className="list-decimal pl-6 space-y-1 mb-2 last:mb-0">{children}</ol>,
                 li: ({ children }) => (
                   <li>
                     {React.Children.map(children as any, (child) => typeof child === 'string' ? <>{transformTextWithPills(child)}</> : child)}
                  </li>
                ),
                 h1: ({ children }) => (
                   <h1 className="mt-4 mb-2 text-2xl md:text-3xl font-semibold">{children}</h1>
                 ),
                 h2: ({ children }) => (
                   <h2 className="mt-4 mb-2 text-xl md:text-2xl font-semibold">{children}</h2>
                 ),
                h3: ({ children }) => (
                  <h3 className="mt-2 mb-2 text-base font-semibold">{children}</h3>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="my-3 border-l-2 border-muted pl-3 italic text-muted-foreground">{children}</blockquote>
                ),
                hr: () => <hr className="my-3 border-border/60" />,
                table: ({ children }) => (
                  <div className="my-3 overflow-x-auto">
                    <table className="w-full text-sm border border-border/60 rounded-md">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => <tr className="border-b border-border/50">{children}</tr>,
                th: ({ children }) => (
                  <th className="text-left px-3 py-2 font-medium">{children}</th>
                ),
                td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/90 underline transition-colors"
                  >
                    {children}
                  </a>
                ),
                code: ({ children }) => (
                  <code className="bg-muted px-2 py-1 rounded text-sm font-mono border">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-muted p-4 rounded-[var(--radius)] overflow-x-auto text-sm border">
                    {children}
                  </pre>
                )
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
          
          {/* Show gradient response indicator for assistant messages */}
          {!isUser && onExpandRequest && (
            <GradientResponseIndicator 
              message={message.content}
              onExpandRequest={() => {
                // Extract the topic they mentioned they can elaborate on
                const expandMatch = message.content.match(/I can (elaborate|go deeper|expand) on ([^.!?]+)/i);
                const expandTopic = expandMatch ? expandMatch[2] : "this topic";
                const expandPrompt = `Can you elaborate more on ${expandTopic.trim()}?`;
                onExpandRequest(expandPrompt);
              }}
            />
          )}
          
          {/* NEW: Show confidence indicator if available */}
          {!isUser && message.smartMetadata?.confidence !== undefined && (
            <div className="mt-2">
              <ConfidenceIndicator score={message.smartMetadata.confidence} />
            </div>
          )}
        </div>
      </div>

      {/* NEW: Show follow-up suggestions if available */}
      {!isUser && message.improvements?.followUp && !isTyping && onFollowUpClick && (
        <div className="flex justify-start mt-2">
          <div className="max-w-[85%]">
            <FollowUpSuggestions
              questions={message.improvements.followUp}
              onSelect={handleFollowUpClick}
            />
          </div>
        </div>
      )}

      {/* Show recommended paid content for assistant messages */}
      {!isUser && recommendedContent && recommendedContent.length > 0 && (
        <div className="flex justify-start mt-2">
          <div className="max-w-[85%] space-y-2">
            {recommendedContent.map((content) => (
              <ContentPaywall
                key={content.id}
                content={content}
                creatorName={creatorName}
                className="max-w-none"
              />
            ))}
          </div>
        </div>
      )}

      {message.attachments && message.attachments.length > 0 && (
        <div className={cn(
          "flex flex-wrap gap-2",
          isUser ? "justify-end" : "justify-start"
        )}>
          {message.attachments.map((file) => (
            <div key={file.id} className="max-w-[200px]">
              <div className={cn(
                "rounded-lg p-2 cursor-pointer hover:bg-muted/80",
                isUser ? "bg-primary/10" : "bg-muted"
              )}>
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0">
                    {getFileIcon(file.mimeType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {file.source === 'google_drive' ? 'Google Drive' : 'Upload'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatMessage;